import _ from 'lodash';
import { EventEmitter } from 'events';
import MQTT from 'mqtt';

import ErrorReporter from '../error_reporter';
import qconsole from 'scripts/lib/qconsole';
import { isDevelopment } from 'scripts/application/lib/environment';

const CAN_LOG_PAYLOADS = isDevelopment() || /\.gladly\.qa/.test(window.location.host);
const DEFAULT_KEEPALIVE = 10; // seconds
const DEFAULT_RECONNECT_PERIOD = 1000; // milliseconds
// Matches the broker's session-expiry max-interval; asking for more is capped anyway.
const SESSION_EXPIRY_SECONDS = 600;
// A SUBACK reason code below this is a granted QoS; at or above it, a refusal.
const SUBACK_FAILURE_FLOOR = 0x80;
// Two refusals this client survives rather than emitting 'unauthorized', which logs
// the agent out and ends any call they are on. They survive for opposite reasons, so
// they get opposite handling.
//
// 0x83, implementation specific error: whatever returns it has chosen it over the
// spec's 0x87, so re-authenticating cannot help — a fresh token carries the same
// permissions and would refuse again. Retired. Which code the deployed broker
// actually returns when a subscribe misses the ESE file realm is not verifiable from
// outside it (the realm is a strict allowlist, so the realistic trigger is an app
// subscription shipping ahead of a realm entry); if it turns out to be 0x87, this
// branch is dead in production and the retirement machinery can go with it.
const SUBACK_PERMANENT_REFUSAL = 0x83;
// 0x97, broker at capacity: transient by definition, and logging out here would take
// the fleet down together while the broker is already degraded. Retried on the same
// backoff as a subscribe timeout.
const SUBACK_QUOTA_EXCEEDED = 0x97;
// Every other refusal keeps the 3.1.1 behaviour of ending the session.
export const MAX_JITTER = 30000;
const JITTER = _.random(0, MAX_JITTER); // random % of jitter for reconnects, up to 30s

export default class MqttClient {
  /**
   * Creates an instance of MqttBackend with the specified MQTT.js client factory.
   *
   * @param url - the url for the mqtt endpoint
   */
  constructor({ url, eventRecorder, clientId }) {
    this._url = url;
    this._mqttClient = null;
    this._topicSubscriptions = {};
    this._emitter = new EventEmitter().setMaxListeners(0);
    this._clientId = clientId;
    this.eventRecorder = eventRecorder;
    this._keepAlive = DEFAULT_KEEPALIVE;
    this._reconnectPeriod = DEFAULT_RECONNECT_PERIOD;

    _.bindAll(this, ['onMessage']);
  }

  // connection uses a cookie to authenticate
  connect(username) {
    this._mqttClient = MQTT.connect(this._url, {
      username,
      clientId: this._clientId,
      clean: false,
      protocolVersion: 5,
      // Under MQTT 5 `clean: false` is not enough to keep a session: lifetime is
      // governed by this interval, which defaults to 0 — the session and its
      // queued messages would be discarded the moment the socket drops. 600s is
      // the broker's own ceiling (config.xml session-expiry max-interval).
      properties: { sessionExpiryInterval: SESSION_EXPIRY_SECONDS },
      resubscribe: false,
      transformWsUrl: (wsUrl, options, client) => {
        client.options.keepalive = this._keepAlive;
        client.options.reconnectPeriod = this._reconnectPeriod;
        qconsole.log(
          `MQTT - connecting with keepalive ${client.options.keepalive} and reconnectPeriod ${client.options.reconnectPeriod}`
        );
        return wsUrl;
      },
    });

    this._registerEventHandlers();
  }

  multiplyDefaultMqttKeepAlive(multiplier) {
    this._keepAlive = DEFAULT_KEEPALIVE * multiplier;
  }

  multiplyDefaultMqttReconnectPeriod(multiplier) {
    this._reconnectPeriod = DEFAULT_RECONNECT_PERIOD * multiplier;
  }

  reset() {
    this.emit('reset');
    if (this._mqttClient) {
      this.unsubscribeAll();
      this._unregisterEventHandlers();
      this._mqttClient.end(() => qconsole.log('MQTT - client shut down'));
      this._mqttClient = null;
    }
  }

  close() {
    this.reset();
    this._emitter.removeAllListeners();
  }

  /**
   * Registers event handlers.
   * @see EventEmitter
   *
   * @param event - one of
   *                * 'disconnect': invoked with the connection to the broker is lost
   *                * 'connect': invoked with the connection with the broker is established
   *                * 'error': invoked when an unexpected error occurs, such as failure to subscribe,
   *                           or a fatal connection error
   *                * 'close': invoked when the connection is explicitly closed by the client
   * @param listener a handler function for the above event
   */
  on(event, listener) {
    this._emitter.on(event, listener);
  }

  off(event, listener) {
    this._emitter.removeListener(event, listener);
  }

  emit(event, ...args) {
    this._emitter.emit(event, ...args);
  }

  /**
   * Remove a listener from the listener array for the specified event.
   * @param event the name of the event
   * @param listener the listener function to remove
   */
  removeListener(event, listener) {
    this._emitter.removeListener(event, listener);
  }

  _registerEventHandlers() {
    this._mqttClient.on('offline', () => {
      this._recordMqttEvent(null, 'MQTT', 'disconnected from broker');
      qconsole.log('MQTT - disconnected from broker');
      clearTimeout(this.reconnectTimeout);

      if (!this._isOffline) {
        // Don't emit when we know we're already offline
        this.emit('disconnect');
      }

      this._isOffline = true;
    });

    // This event typically will happen together with "offline"
    this._mqttClient.on('close', () => {
      if (!this._isOffline) {
        this._isOffline = true;
        this.emit('disconnect');
      }
    });

    this._mqttClient.on('connect', () => {
      this._recordMqttEvent(null, 'MQTT', 'connected to broker');
      qconsole.log('MQTT - connected to broker');
      this.emit('connect');

      if (this._isOffline) {
        // Only emit on reconnect
        this._isOffline = false;
        this._resubscribe();

        this.reconnectTimeout = setTimeout(() => this.emit('reconnect'), JITTER);
      }
    });

    this._mqttClient.on('error', err => {
      if (err.message && err.message === 'Connection refused: Not authorized') {
        qconsole.log('MQTT - unauthorized connection to mqtt');
        this.emit('unauthorized');
        return;
      }

      ErrorReporter.reportError(err, { message: 'MQTT - unexpected error occurred' });
      this.emit('error', err);
    });

    this._mqttClient.on('message', this.onMessage);
  }

  get isOffline() {
    return this._isOffline;
  }

  onMessage(topic, message) {
    let json = JSON.parse(message);
    if (CAN_LOG_PAYLOADS) {
      qconsole.log(`MQTT - received message with ${topic}`, json);
    } else {
      qconsole.log(`MQTT - received message with ${topic}`);
    }
    this._eachTopicSubscription(t => t.message(json, topic));

    this._recordMqttEvent(json.correlationId, 'MSG', topic);
  }

  publish(topic, message, attempt = 1) {
    if (CAN_LOG_PAYLOADS) {
      qconsole.log(`MQTT - publishing message with ${topic}`, message);
    } else {
      qconsole.log(`MQTT - publishing message with ${topic}`);
    }

    this._recordMqttEvent(message.correlationId, 'PUB', topic);
    this._mqttClient.publish(topic, JSON.stringify(message), { qos: 1 }, err => {
      if (err && err.toString() === 'Error: client disconnecting' && attempt < 11) {
        setTimeout(() => this.publish(topic, message, attempt + 1), attempt * 100);
      } else if (err) {
        qconsole.error(`MQTT - publishing to ${topic} failed`, err);
      } else {
        qconsole.log(`MQTT - publishing to ${topic} acknowledged`);
      }
    });
  }

  /**
   * Subscribes the provided `onReceive` handler (a subscriber) to the topic.
   *
   *
   * @param topic - the topic to subscribe to, e.g. "server/v1/customer/123/profile"
   * @param onReceive - a function to handle messages for the topic.
   *                    It will be called every time a new message arrives.
   * @param cb - an optional callback to be invoked when subscription is acknowledged
   */
  subscribe(topic, onReceive, cb) {
    if (!_.isFunction(onReceive)) {
      throw new Error('onReceive must be a function');
    }

    if (!_.isUndefined(cb) && !_.isFunction(cb)) {
      throw new Error('cb must be a function');
    }

    this._getTopicSubscription(topic).add(onReceive, cb);
  }

  _getTopicSubscription(topic) {
    if (this._topicSubscriptions[topic]) {
      return this._topicSubscriptions[topic];
    }
    let topicSubscription = (this._topicSubscriptions[topic] = new TopicSubscription(topic));
    topicSubscription.onEmpty(() => this._rmTopicSubscription(topic));
    this._subscribe(topic);
    return topicSubscription;
  }

  _subscribe(topic, backoffDelay = 30000) {
    let topicSubscription = this._topicSubscriptions[topic];
    if (!topicSubscription) {
      return;
    }

    // Don't try to subscribe after `this.reset()` has happened. This can
    // happen if the subscribe retry timeout occurs before the mqtt
    // disconnect event is fired DEV-13208.
    // Also, do not try to subscribe when the client is offline (may happen during frequent
    // disconnect-reconnects)
    if (!this._mqttClient || this._isOffline) {
      if (this._isOffline) {
        qconsole.log(`MQTT - attempt to subscribe to ${topic} while offline/disconnected`);
      }
      return;
    }

    let timeoutDelay = Math.floor(backoffDelay + Math.random() * backoffDelay);
    let timeout = setTimeout(() => {
      let errMsg = 'MQTT - subscribe timeout';
      ErrorReporter.reportError(new Error(errMsg), {
        tags: { mqttTopic: topic, backoffDelay, timeoutDelay },
      });
      this._subscribe(topic, Math.min(backoffDelay * 2, 60000));
    }, timeoutDelay);

    let cancelTimeout = () => clearTimeout(timeout);
    this.on('disconnect', cancelTimeout);

    // Every SUBSCRIBE supersedes the one before it on this subscription: the timeout
    // above re-enters without waiting for a SUBACK, so more than one can be
    // outstanding. Acting on a superseded one settles the subscription — grants it,
    // retires it, or forks another retry chain — on stale information.
    const attempt = topicSubscription.nextAttempt();

    this._recordMqttEvent(null, 'SUB', topic);
    this._mqttClient.subscribe(topic, { qos: 1 }, (err, granted) => {
      this.off('disconnect', cancelTimeout);
      cancelTimeout();

      // Two ways this SUBACK can have stopped being the one that speaks for the
      // topic: the subscription was emptied and subscribed again, replacing the
      // instance, or the timeout above minted a newer attempt on this one. The
      // instance half matters on its own — a replacement resets `attempt` to 0, so a
      // dead instance's closure can hold an attempt number that is still "current"
      // on itself while a different instance is the one registered.
      const isLiveAttempt =
        this._topicSubscriptions[topic] === topicSubscription && topicSubscription.isCurrentAttempt(attempt);

      if (err) {
        // Reported whether or not this attempt is still live: a transport error
        // happened either way, and the guard is about not settling the subscription
        // on stale information rather than about suppressing the report.
        this._recordMqttEvent(null, 'SUBERR', topic);
        ErrorReporter.reportError(err, { message: 'MQTT - could not subscribe', tags: { mqttTopic: topic } });

        if (isLiveAttempt) {
          topicSubscription.fail(err);
        }

        return;
      }

      if (!isLiveAttempt) {
        return;
      }

      if (!granted || !granted[0]) {
        this._recordMqttEvent(null, 'SUBERR', topic);
        const errMsg = 'MQTT - subscription not granted';
        ErrorReporter.reportError(new Error(errMsg), { tags: { granted, mqttTopic: topic } });
        // Not `err`: the branch above returns on every truthy err, so it is null
        // here, and failing with a falsy error reads as success to subscribers.
        return topicSubscription.fail(new Error(errMsg));
      }

      // `qos` is a misnomer from the mqtt library: it carries the SUBACK reason
      // code, which is the granted QoS on success. MQTT 3.1.1 reported every
      // refusal as 128; MQTT 5 splits that into a range, so this has to test the
      // range rather than the one value — a `=== 128` check would read 0x87 (not
      // authorized) as a granted QoS of 135 and report the subscription ready.
      const reasonCode = granted[0].qos;
      if (reasonCode >= SUBACK_FAILURE_FLOOR) {
        return this._handleSubackFailure(topic, reasonCode, topicSubscription, backoffDelay);
      }

      this._recordMqttEvent(null, 'SUBACK', topic);
      qconsole.log(`MQTT - subscription to ${granted[0].topic} granted with QoS ${granted[0].qos}`);

      return topicSubscription.ready();
    });

    return topicSubscription;
  }

  /**
   * Removes the topic's `onReceive` handler (a subscriber).
   *
   * @param topic - the topic to remove the handler from.
   * @param onReceive - the handler to remove. It must be the exact same handler as was passed in `subscribe`,
   *                    i.e. it mas the `===` identity test
   */
  unsubscribe(topic, onReceive) {
    this._getTopicSubscription(topic).rm(onReceive);
  }

  unsubscribeAll() {
    this._eachTopicSubscription(t => t.rmAll());
  }

  // Waits out the backoff before resubscribing, on the same jittered schedule as the
  // subscribe timeout. The delay has to be applied here: _subscribe sends its
  // SUBSCRIBE immediately and uses backoffDelay only to time its own giving-up, and
  // the ack callback has already cancelled that timeout — so calling it directly
  // would retry in a tight loop against a broker that is already out of capacity.
  //
  // Cancelled on disconnect, which leaves _resubscribe as the only reconnect path.
  _retrySubscribeAfterBackoff(topic, backoffDelay) {
    const nextBackoffDelay = Math.min(backoffDelay * 2, 60000);
    const retryDelay = Math.floor(backoffDelay + Math.random() * backoffDelay);
    const subscription = this._topicSubscriptions[topic];

    let retryTimeout = setTimeout(() => {
      this.off('disconnect', cancelRetry);

      // The topic can be unsubscribed and subscribed again while this waits, and the
      // replacement issues its own SUBSCRIBE. Retrying for a subscription that is no
      // longer the live one would just duplicate it.
      if (this._topicSubscriptions[topic] !== subscription) {
        return;
      }

      this._subscribe(topic, nextBackoffDelay);
    }, retryDelay);

    let cancelRetry = () => {
      clearTimeout(retryTimeout);
      this.off('disconnect', cancelRetry);
    };

    this.on('disconnect', cancelRetry);
  }

  // Triage for a SUBACK reason code at or above SUBACK_FAILURE_FLOOR. Returns the
  // value _subscribe's callback should return.
  _handleSubackFailure(topic, reasonCode, topicSubscription, backoffDelay) {
    this._recordMqttEvent(null, 'SUBERR', topic);

    const tags = { mqttTopic: topic, reasonCode: formatReasonCode(reasonCode) };

    // Transient, so retry rather than retiring the topic for the life of the tab.
    if (reasonCode === SUBACK_QUOTA_EXCEEDED) {
      // Once per subscription, re-armed by a grant — not once per attempt. The retry
      // chain does not terminate on its own, and the pre-existing subscribe-timeout
      // loop retries at this same cadence, a rate the team already judged too noisy
      // to keep: configure_logging.js drops 'MQTT - subscribe timeout' in
      // ignoreErrors. This keeps the signal that capacity pressure started without
      // the stream. A disconnect ends the chain without re-arming, deliberately —
      // reconnecting into the same shortage is the same incident.
      if (!topicSubscription.hasReportedCapacityRefusal) {
        topicSubscription.hasReportedCapacityRefusal = true;
        ErrorReporter.reportError(new Error('MQTT - subscription refused, broker at capacity'), { tags });
      }

      // Settle now, and retry anyway. Leaving the subscription pending would hold
      // StandardGateway.request()'s fetch behind a chain with no bound — an empty
      // panel for as long as capacity is short, where 3.1.1 read 0x97 as a granted
      // QoS and fetched immediately. Subscribers attach their message handler in
      // add(), separately from the ack, so the retry chain keeps them and a grant
      // resumes broadcasts.
      //
      // What the grant does not do is close the window. fail() consumes the one-shot
      // 'ready', so request() fetched at T0 and nothing re-fetches when the grant
      // lands a backoff or more later: whatever changed in between is lost, and the
      // panel looks correct while being wrong. Still the best of the three — 3.1.1
      // went permanently deaf, and deferring the fetch showed an empty panel — so
      // re-arming the fetch here needs a decision about what reports the gap, not a
      // one-line change.
      topicSubscription.fail(new Error(`Subscription refused (${formatReasonCode(reasonCode)})`));
      this._retrySubscribeAfterBackoff(topic, backoffDelay);
      return undefined;
    }

    if (reasonCode === SUBACK_PERMANENT_REFUSAL) {
      ErrorReporter.reportError(new Error('MQTT - subscription refused'), { tags });
      return topicSubscription.refuse(new Error(`Subscription refused (${formatReasonCode(reasonCode)})`));
    }

    // Reported as well as logged: qconsole.error is a straight pass-through to the
    // native console and never reaches Sentry, so without this the branch that ends
    // the agent's session would be the only one with no issue attached to it.
    ErrorReporter.reportError(new Error('MQTT - unauthorized subscription'), { tags });
    qconsole.error(`MQTT - unauthenticated or unauthorized subscription to ${topic}`);
    this.emit('unauthorized');

    return topicSubscription.fail(new Error('Not authorized'));
  }

  _resubscribe() {
    this._eachTopicSubscription((subscription, topic) => {
      if (!subscription.isRefused) {
        this._subscribe(topic);
      }
    });
  }

  _eachTopicSubscription(callback) {
    _.forEach(this._topicSubscriptions, (subscription, pattern) => {
      if (subscription) {
        // guard against subscription being removed by a previous invocation
        // of the callback
        // DEV-9611
        callback(subscription, pattern);
      }
    });
  }

  _rmTopicSubscription(topic) {
    delete this._topicSubscriptions[topic];
    this._recordMqttEvent(null, 'UNSUB', topic);
    this._mqttClient.unsubscribe(topic, () => qconsole.log(`MQTT - unsubscription from ${topic} acknowledged`));
  }

  _recordMqttEvent(correlationId, type, topic) {
    this.eventRecorder && this.eventRecorder.recordEvent(correlationId, `${type} ${topic}`);
  }

  _unregisterEventHandlers() {
    if (!this._mqttClient) return;

    this._mqttClient.off('message', this.onMessage);
  }
}

export class TopicSubscription extends EventEmitter {
  constructor(pattern) {
    super();
    this.pattern = pattern;
    this.regex = createTopicRegex(pattern);
    this.isReady = false;
    this.isRefused = false;
    this.refusalError = null;
    this.attempt = 0;
    this.hasReportedCapacityRefusal = false;
  }

  add(onMessage, onReady) {
    if (this.listeners('message').includes(onMessage)) {
      qconsole.log(`MQTT - ${this.pattern} already has this subscriber`);
    } else {
      this.on('message', onMessage);
    }

    if (onReady) {
      this._onReady(onReady);
    }
  }

  rm(onMessage) {
    this.removeListener('message', onMessage);
    if (this.listenerCount('message') === 0) {
      this._empty();
    }
  }

  rmAll() {
    this.removeAllListeners('message');
    this._empty();
  }

  onEmpty(onEmpty) {
    this.once('empty', onEmpty);
  }

  // Mints the token for a new SUBSCRIBE, superseding any still outstanding.
  nextAttempt() {
    this.attempt += 1;

    return this.attempt;
  }

  isCurrentAttempt(attempt) {
    return this.attempt === attempt;
  }

  ready() {
    // Re-arms capacity reporting. A grant is the only thing that does: a disconnect
    // ends the retry chain too, but reconnecting into the same shortage is the same
    // incident and should not report a second time.
    this.hasReportedCapacityRefusal = false;
    this.isReady = true;
    this.emit('ready', null);
  }

  fail(err) {
    this.emit('ready', err);
  }

  // Refused for a reason a reconnect cannot change, so this instance is not
  // resubscribed and the error is retained: later subscribers reuse the instance and
  // have no second 'ready' to await.
  //
  // Scoped to the instance, not the topic. _empty() drops the entry once the last
  // subscriber leaves, so a topic bound to a UI panel is subscribed afresh on the
  // next mount and refuses again. Suppressing that would mean remembering refusals
  // across instances, which is a broader change than this one.
  refuse(err) {
    // A retired subscription is no longer ready, and saying so matters: _onReady
    // reads isReady first, so a topic granted on an earlier connection and refused on
    // a later one would keep handing later subscribers success.
    this.isReady = false;
    this.isRefused = true;
    this.refusalError = err;
    this.fail(err);
  }

  message(message, topic) {
    if (!this.regex.test(topic)) {
      return;
    }

    // not using `emit` b/c of custom error handling
    this.listeners('message').forEach(listener => {
      try {
        listener(message, topic);
      } catch (err) {
        let errParams = {
          message: 'MQTT - could not process incoming message',
          tags: { mqttTopic: topic },
          extra: {
            topic,
            messageLength: JSON.stringify(message).length,
          },
        };

        // NOTE: only log payload on internal domains to protect client security
        if (/\.gladly\.qa/.test(window.location.host)) {
          errParams.extra.message = message;
        }
        ErrorReporter.reportError(err, errParams);
      }
    });
  }

  _onReady(onReady) {
    if (this.isReady) {
      setImmediate(onReady);
    } else if (this.isRefused) {
      // 'ready' already fired and will not fire again, so waiting on it would hang
      // this subscriber forever. Hand it the refusal instead.
      setImmediate(() => onReady(this.refusalError));
    } else {
      this.once('ready', onReady);
    }
  }

  _empty() {
    this.emit('empty');
    this.removeAllListeners();
  }
}

/**
 * Replaces wildcards, + and #, with regular expressions
 * @param topic with wildcards
 * @returns {RegExp}
 */
function createTopicRegex(topic) {
  return new RegExp(
    `^${topic
      .replace(/[.*?^${}()|[\]\\]/g, '\\$&') // escape all RegExp tokens except for `+`
      .replace(/\+/g, '[^/]+')
      .replace('#', '.*')}$`
  );
}

// Hex to match the MQTT 5 reason-code table an on-call engineer would look it up in.
// The mqtt library hands these over as decimal numbers.
function formatReasonCode(reasonCode) {
  return `0x${reasonCode.toString(16).toUpperCase()}`;
}
